/* eslint-disable */
// Optimized GraphQL queries for performance improvements
// These queries solve the N+1 problem by batching image data with products

// ============================================================================
// OPTIMIZED PRODUCT QUERIES WITH IMAGES
// ============================================================================

export const listProductsWithPrimaryImages = /* GraphQL */ `
  query ListProductsWithPrimaryImages(
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        description
        shortDescription
        categoryId
        category {
          id
          name
          __typename
        }
        price
        discountPrice
        stock
        sku
        isActive
        isFeatured
        tags
        farmingMethod
        harvestDate
        expiryDate
        images(filter: { isPrimary: { eq: true } }, limit: 1) {
          items {
            id
            url
            altText
            isPrimary
            __typename
          }
          __typename
        }
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

export const listProductsWithAllImages = /* GraphQL */ `
  query ListProductsWithAllImages(
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        description
        shortDescription
        categoryId
        category {
          id
          name
          __typename
        }
        price
        discountPrice
        stock
        sku
        weight
        dimensions
        isActive
        isFeatured
        tags
        nutritionalInfo
        farmingMethod
        harvestDate
        expiryDate
        images {
          items {
            id
            url
            altText
            isPrimary
            order
            __typename
          }
          __typename
        }
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

export const getProductWithImages = /* GraphQL */ `
  query GetProductWithImages($id: ID!) {
    getProduct(id: $id) {
      id
      name
      description
      shortDescription
      categoryId
      category {
        id
        name
        description
        isActive
        displayOrder
        __typename
      }
      price
      discountPrice
      stock
      sku
      weight
      dimensions
      isActive
      isFeatured
      tags
      nutritionalInfo
      farmingMethod
      harvestDate
      expiryDate
      images {
        items {
          id
          url
          altText
          isPrimary
          order
          __typename
        }
        __typename
      }
      videos {
        items {
          id
          url
          title
          thumbnail
          duration
          __typename
        }
        __typename
      }
      variants {
        items {
          id
          name
          price
          stock
          sku
          __typename
        }
        __typename
      }
      reviews {
        items {
          id
          rating
          title
          comment
          verified
          user {
            name
            __typename
          }
          createdAt
          __typename
        }
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;

// ============================================================================
// CATEGORY-BASED OPTIMIZED QUERIES
// ============================================================================

export const productsByCategoryWithImages = /* GraphQL */ `
  query ProductsByCategoryWithImages(
    $categoryId: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    productsByCategoryId(
      categoryId: $categoryId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        description
        shortDescription
        categoryId
        price
        discountPrice
        stock
        sku
        isActive
        isFeatured
        tags
        farmingMethod
        harvestDate
        expiryDate
        images(filter: { isPrimary: { eq: true } }, limit: 1) {
          items {
            id
            url
            altText
            isPrimary
            __typename
          }
          __typename
        }
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

// ============================================================================
// FEATURED PRODUCTS WITH IMAGES
// ============================================================================

export const listFeaturedProductsWithImages = /* GraphQL */ `
  query ListFeaturedProductsWithImages(
    $filter: ModelFeaturedProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listFeaturedProducts(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        displayOrder
        isActive
        startDate
        endDate
        product {
          id
          name
          description
          shortDescription
          price
          discountPrice
          stock
          sku
          isActive
          isFeatured
          tags
          farmingMethod
          images(filter: { isPrimary: { eq: true } }, limit: 1) {
            items {
              id
              url
              altText
              isPrimary
              __typename
            }
            __typename
          }
          category {
            id
            name
            __typename
          }
          __typename
        }
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

// ============================================================================
// MINIMAL PRODUCT LIST FOR QUICK LOADING
// ============================================================================

export const listProductsMinimal = /* GraphQL */ `
  query ListProductsMinimal(
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        shortDescription
        price
        discountPrice
        stock
        isActive
        isFeatured
        images(filter: { isPrimary: { eq: true } }, limit: 1) {
          items {
            url
            altText
            __typename
          }
          __typename
        }
        __typename
      }
      nextToken
      __typename
    }
  }
`;

// ============================================================================
// ADMIN DASHBOARD QUERIES
// ============================================================================

export const listProductsForAdmin = /* GraphQL */ `
  query ListProductsForAdmin(
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        description
        shortDescription
        categoryId
        category {
          id
          name
          __typename
        }
        price
        discountPrice
        stock
        sku
        isActive
        isFeatured
        farmingMethod
        harvestDate
        expiryDate
        images(filter: { isPrimary: { eq: true } }, limit: 1) {
          items {
            id
            url
            altText
            isPrimary
            __typename
          }
          __typename
        }
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

// ============================================================================
// SEARCH QUERIES
// ============================================================================

export const searchProductsWithImages = /* GraphQL */ `
  query SearchProductsWithImages(
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        description
        shortDescription
        categoryId
        price
        discountPrice
        stock
        sku
        isActive
        isFeatured
        tags
        farmingMethod
        images(filter: { isPrimary: { eq: true } }, limit: 1) {
          items {
            id
            url
            altText
            isPrimary
            __typename
          }
          __typename
        }
        category {
          id
          name
          __typename
        }
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;